import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { Platform } from 'react-native';

interface PlayerVisibilityContextType {
  isPlayerVisible: boolean;
  isTransitioning: boolean;
  showPlayer: () => void;
  hidePlayer: () => void;
}

const PlayerVisibilityContext = createContext<PlayerVisibilityContextType | undefined>(undefined);

export function PlayerVisibilityProvider({ children }: { children: React.ReactNode }) {
  const [isPlayerVisible, setIsPlayerVisible] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const showPlayer = useCallback(() => {
    setIsTransitioning(true);
    setIsPlayerVisible(true);

    // Mark transition complete after animation duration (300ms)
    setTimeout(() => {
      setIsTransitioning(false);
    }, 300);
  }, []);

  const hidePlayer = useCallback(() => {
    setIsTransitioning(true);
    setIsPlayerVisible(false);

    // Mark transition complete after animation duration (300ms)
    setTimeout(() => {
      setIsTransitioning(false);
    }, 300);
  }, []);

  const value = useMemo(
    () => ({
      isPlayerVisible,
      isTransitioning,
      showPlayer,
      hidePlayer,
    }),
    [isPlayerVisible, isTransitioning, showPlayer, hidePlayer]
  );

  return (
    <PlayerVisibilityContext.Provider value={value}>
      {children}
    </PlayerVisibilityContext.Provider>
  );
}

export function usePlayerVisibility() {
  const context = useContext(PlayerVisibilityContext);

  if (context === undefined) {
    // Fallback for cases where provider is not in tree
    if (Platform.OS === 'web') {
      // Web has its own event-based system
      return {
        isPlayerVisible: false,
        isTransitioning: false,
        showPlayer: () => {},
        hidePlayer: () => {},
      };
    }

    throw new Error('usePlayerVisibility must be used within a PlayerVisibilityProvider');
  }

  return context;
}
